import { Button } from "@/components/ui/button";
import {
	siteDesignInFlight,
	type ChatSiteDesign,
} from "./messages.js";
import { SiteFavicon, hostFromUrl } from "./SiteFavicon.js";
import { useEffect, useRef, useState } from "react";

type Props = {
	rows: ChatSiteDesign[];
	onOpenSitePreview?: (url: string) => void;
	onPatch?: (siteId: string, patch: Partial<ChatSiteDesign>) => void;
};

const PHASE_COPY: Record<string, string> = {
	started: "Learning the site…",
	inventorying: "Reading what the site does…",
	briefing: "Deciding what to keep and add…",
	designing: "Laying out the new site from the brand…",
	media: "Making photographs that match the brand…",
	framing: "Opening the Astro project…",
	awaiting_design: "Writing the Astro project…",
	generating: "Writing the Astro project…",
	ready: "Preview ready",
	failed: "Something went wrong",
	progress: "Working on the redesign…",
};

export function DesignSiteCards({ rows, onOpenSitePreview, onPatch }: Props) {
	useSiteDesignPoll(rows, onPatch);
	if (rows.length === 0) return null;
	return (
		<div className="flex flex-col gap-3">
			{rows.map((row) => (
				<DesignSiteCard
					key={row.siteId}
					row={row}
					onOpenSitePreview={onOpenSitePreview}
					onPatch={onPatch}
				/>
			))}
		</div>
	);
}

function canvasUrl(row: ChatSiteDesign): string {
	return row.previewUrl ?? `/api/sites/${row.siteId}/preview/index.html`;
}

function DesignSiteCard({
	row,
	onOpenSitePreview,
}: {
	row: ChatSiteDesign;
	onOpenSitePreview?: (url: string) => void;
	onPatch?: (siteId: string, patch: Partial<ChatSiteDesign>) => void;
}) {
	const host = hostFromUrl(row.sourceUrl) || row.name;
	const ready = row.phase === "ready" || row.status === "preview_ready";
	const writing = siteDesignInFlight(row);

	return (
		<article className="mc-clone-card rounded-xl border border-border bg-card p-4">
			<header className="mb-3 flex items-center gap-2">
				<SiteFavicon url={row.sourceUrl} />
				<div className="min-w-0">
					<p className="truncate text-sm font-medium">{row.name || host}</p>
					<p className="text-muted-foreground text-xs">New site · Astro project</p>
				</div>
			</header>
			<p className="text-muted-foreground mb-2 text-sm">
				{PHASE_COPY[row.phase] ?? PHASE_COPY.progress}
			</p>
			{writing ? (
				<div className="mb-3 h-1.5 overflow-hidden rounded-full bg-muted">
					<div
						className="bg-foreground h-full transition-[width]"
						style={{ width: `${Math.max(4, Math.min(100, row.progress))}%` }}
					/>
				</div>
			) : null}
			{row.lastError ? (
				<p className="text-destructive mb-2 text-sm">{row.lastError}</p>
			) : null}
			<div className="mt-2 flex flex-wrap gap-2">
				<Button
					type="button"
					onClick={() => onOpenSitePreview?.(canvasUrl(row))}
				>
					{ready ? "Open preview" : "Open studio"}
				</Button>
			</div>
		</article>
	);
}

export function useSiteDesignPoll(
	rows: readonly ChatSiteDesign[],
	onPatch?: (siteId: string, patch: Partial<ChatSiteDesign>) => void,
): void {
	const watch = rows
		.filter(siteDesignInFlight)
		.map((row) => row.siteId)
		.sort()
		.join(",");
	const rowsRef = useRef(rows);
	rowsRef.current = rows;
	const onPatchRef = useRef(onPatch);
	onPatchRef.current = onPatch;

	useEffect(() => {
		if (!watch) return;
		const ids = watch.split(",");
		let cancelled = false;
		const tick = async () => {
			await Promise.all(
				ids.map(async (siteId) => {
					try {
						const response = await fetch(`/api/sites/${siteId}/status`, {
							credentials: "include",
						});
						if (!response.ok || cancelled) return;
						const json: unknown = await response.json();
						if (!json || typeof json !== "object") return;
						const rec = json as { ok?: unknown; site?: Record<string, unknown> };
						if (rec.ok !== true || !rec.site || cancelled) return;
						const site = rec.site;
						if (site.origin !== "redesign" && site.origin !== undefined) return;
						onPatchRef.current?.(siteId, {
							name: typeof site.name === "string" ? site.name : undefined,
							sourceUrl:
								typeof site.sourceUrl === "string" ? site.sourceUrl : undefined,
							phase: phaseFromStatus(site),
							status: typeof site.status === "string" ? site.status : "cloning",
							progress: typeof site.progress === "number" ? site.progress : 0,
							previewUrl:
								typeof site.previewUrl === "string" ? site.previewUrl : null,
							lastError:
								typeof site.lastError === "string" ? site.lastError : null,
							keep: asDecisions(site.keep),
							add: asDecisions(site.add),
							drop: asDecisions(site.drop),
							stills: asStills(site.stills),
							lastFile: typeof site.lastFile === "string" ? site.lastFile : null,
							filesWritten: asFiles(site.filesWritten),
						});
					} catch {
						/* ignore */
					}
				}),
			);
		};
		void tick();
		const timer = window.setInterval(() => void tick(), 4000);
		return () => {
			cancelled = true;
			window.clearInterval(timer);
		};
	}, [watch]);
}

function phaseFromStatus(site: Record<string, unknown>): ChatSiteDesign["phase"] {
	const phase = typeof site.phase === "string" ? site.phase : "";
	if (site.status === "preview_ready") return "ready";
	if (site.status === "failed") return "failed";
	if (
		phase === "inventorying" ||
		phase === "briefing" ||
		phase === "designing" ||
		phase === "media" ||
		phase === "framing" ||
		phase === "awaiting_design" ||
		phase === "generating"
	) {
		return phase;
	}
	return "progress";
}

function asDecisions(raw: unknown): Array<{ label: string; reason: string }> {
	if (!Array.isArray(raw)) return [];
	return raw.flatMap((row) => {
		if (!row || typeof row !== "object") return [];
		const rec = row as { label?: unknown; reason?: unknown };
		if (typeof rec.label !== "string") return [];
		return [{ label: rec.label, reason: typeof rec.reason === "string" ? rec.reason : "" }];
	});
}

function asStills(
	raw: unknown,
): Array<{ id: string; title: string; page: string }> {
	if (!Array.isArray(raw)) return [];
	return raw.flatMap((row) => {
		if (!row || typeof row !== "object") return [];
		const rec = row as { id?: unknown; title?: unknown; page?: unknown };
		if (typeof rec.id !== "string") return [];
		return [
			{
				id: rec.id,
				title: typeof rec.title === "string" ? rec.title : rec.id,
				page: typeof rec.page === "string" ? rec.page : "/",
			},
		];
	});
}

function asFiles(raw: unknown): string[] {
	if (!Array.isArray(raw)) return [];
	return raw.filter((row): row is string => typeof row === "string");
}
